import { useState, useEffect, useMemo, useRef } from 'react';
import { useEditorStore, useTimelineStore, useMovieStore, useSelectionStore } from '../../stores';
import type { Keyframe, Tween, TimelineLayer, SceneObject, SceneResponse } from '../../types';
import { useSignalR } from '../../hooks/useSignalR';
import { getLastKeyframeFrame, sortTimelineLayers } from '../../util/timeline';
import { createEditorPlaybackTimeline } from '../../util/editorPlaybackTimeline';
import { seedNewKeyframeState, sceneObjectRestPoseState } from '../../util/newKeyframeSeed';
import './Timeline.css';
import { Button, Tooltip, ContextMenu } from '@radix-ui/themes';
import { ArrowLeftIcon, ArrowRightIcon, PauseIcon, PlayIcon, StopIcon, LayersIcon, PlusIcon, PlusCircledIcon, MinusCircledIcon } from '@radix-ui/react-icons';
import { IconButton } from '@radix-ui/themes';
import { ConfirmModal } from '../ui/ConfirmModal';
import { useTranslation } from '../../i18n';

const FRAME_WIDTH = 12;
const DRAG_THRESHOLD = 3;

function canAddTweenAtFrame(
  layerId: string,
  frame: number,
  keyframes: Record<string, Keyframe[]>,
  tweens: Record<string, Tween[]>,
): boolean {
  const layerKeyframes = keyframes[layerId] ?? [];
  if (layerKeyframes.length < 2) return false;

  const sorted = [...layerKeyframes].sort((a, b) => a.frame - b.frame);
  if (frame <= sorted[0].frame || frame >= sorted[sorted.length - 1].frame) return false;

  const startKeyframe = [...layerKeyframes]
    .filter((kf) => kf.frame <= frame)
    .sort((a, b) => b.frame - a.frame)[0];
  const endKeyframe = [...layerKeyframes]
    .filter((kf) => kf.frame > frame)
    .sort((a, b) => a.frame - b.frame)[0];

  if (!startKeyframe || !endKeyframe) return false;

  const layerTweens = tweens[layerId] ?? [];
  if (layerTweens.some((tw) => tw.startFrame <= frame && tw.endFrame > frame)) return false;
  if (layerTweens.some(
    (tw) => tw.startFrame === startKeyframe.frame && tw.endFrame === endKeyframe.frame,
  )) {
    return false;
  }

  return true;
}

function canAddKeyframeAtFrame(
  layerId: string,
  frame: number,
  keyframes: Record<string, Keyframe[]>,
  selectedKeyframe: { layerId: string; frame: number } | null,
): boolean {
  if (selectedKeyframe) return false;
  const layerKeyframes = keyframes[layerId] ?? [];
  return !layerKeyframes.some((kf) => kf.frame === frame);
}

export function Timeline() {
  const { isPlaying, currentFrame, fps, play, pause, stop, setFrame, nextFrame, prevFrame } = useEditorStore();
  const { totalFrames, setTotalFrames, keyframes } = useTimelineStore();

  const canPlay = useMemo(() => getLastKeyframeFrame(keyframes) !== null, [keyframes]);

  // Keyframe yokken veya son keyframe silinirken oynatmayı kapat
  useEffect(() => {
    if (!isPlaying || canPlay) return;
    pause();
  }, [isPlaying, canPlay, pause]);

  // Play: timeline store verisiyle gsap.timeline — playhead son keyframe'e kadar; bitince pause
  useEffect(() => {
    if (!isPlaying) return;

    const ed = useEditorStore.getState();
    const tlStore = useTimelineStore.getState();

    const timeline = createEditorPlaybackTimeline({
      keyframes: tlStore.keyframes,
      totalFrames: tlStore.totalFrames,
      startFrame: ed.currentFrame,
      fps: ed.fps,
      setFrame: (frame) => useEditorStore.getState().setFrame(frame),
      onComplete: () => useEditorStore.getState().pause(),
    });

    return () => {
      timeline?.kill();
    };
  }, [isPlaying, fps, totalFrames, keyframes]);

  return (
    <div className="timeline">
      <TimelineHeader
        isPlaying={isPlaying}
        canPlay={canPlay}
        currentFrame={currentFrame}
        fps={fps}
        totalFrames={totalFrames}
        onPlay={play}
        onPause={pause}
        onStop={stop}
        onPrevFrame={prevFrame}
        onNextFrame={nextFrame}
        onTotalFramesChange={setTotalFrames}
      />
      <div className="timeline-body">
        <TimelineLayers />
        <TimelineFrames currentFrame={currentFrame} totalFrames={totalFrames} onFrameClick={setFrame} />
      </div>
    </div>
  );
}

interface TimelineHeaderProps {
  isPlaying: boolean;
  canPlay: boolean;
  currentFrame: number;
  fps: number;
  totalFrames: number;
  onPlay: () => void;
  onPause: () => void;
  onStop: () => void;
  onPrevFrame: () => void;
  onNextFrame: () => void;
  onTotalFramesChange: (frames: number) => void;
}

function TimelineHeader({
  isPlaying,
  canPlay,
  currentFrame,
  fps,
  totalFrames,
  onPlay,
  onPause,
  onStop,
  onPrevFrame,
  onNextFrame,
  onTotalFramesChange,
}: TimelineHeaderProps) {
  const { t } = useTranslation();
  const time = (currentFrame / fps).toFixed(2);
  const {
    addKeyframe,
    addTween,
    deleteKeyframe,
    deleteTween,
    keyframes,
    tweens,
    selectedKeyframe,
    selectedTween,
    setSelectedKeyframe,
    setSelectedTween,
  } = useTimelineStore();
  const { currentScene, currentMovieResponse, currentLayer } = useMovieStore();
  const { keyFrameSave, keyFrameDelete, tweenSave, tweenDelete } = useSignalR();
  const [isCreatingKeyframe, setIsCreatingKeyframe] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const canAddTween = useMemo(() => {
    if (!currentLayer || selectedTween) return false;
    return canAddTweenAtFrame(currentLayer.id, currentFrame, keyframes, tweens);
  }, [currentLayer, currentFrame, keyframes, tweens, selectedTween]);

  const canAddKeyframe = useMemo(() => {
    if (!currentLayer) return false;
    return canAddKeyframeAtFrame(currentLayer.id, currentFrame, keyframes, selectedKeyframe);
  }, [currentLayer, currentFrame, keyframes, selectedKeyframe]);

  const handleAddKeyframe = async () => {
    if (!currentLayer || !currentScene || !currentMovieResponse) return;
    if (!canAddKeyframeAtFrame(currentLayer.id, currentFrame, keyframes, selectedKeyframe)) return;

    const layerIdNum = parseInt(currentLayer.id);
    if (isNaN(layerIdNum)) {
      console.warn('Cannot add keyframe: Invalid layer id', currentLayer.id);
      return;
    }

    setIsCreatingKeyframe(true);
    try {
      const keyFrameRequest = {
        id: 0,
        movieId: currentMovieResponse.id,
        sceneId: parseInt(currentScene.id),
        layerId: layerIdNum,
        frameNumber: currentFrame,
        objectStates: [],
      };
      
      const createdKeyframe = await keyFrameSave(keyFrameRequest);

      const layerKfs = keyframes[currentLayer.id] ?? [];
      const { state, objectStates } = seedNewKeyframeState(
        currentLayer,
        currentFrame,
        layerKfs,
        currentScene
      );

      const newKeyframe: Keyframe = {
        id: String(createdKeyframe.id),
        layerId: currentLayer.id,
        frame: currentFrame,
        state,
        ...(objectStates ? { objectStates } : {}),
      };

      addKeyframe(newKeyframe);
    } catch (error) {
      console.error('Failed to create keyframe:', error);
    } finally {
      setIsCreatingKeyframe(false);
    }
  };

  const handleAddTween = async () => {
    if (!currentLayer || !currentScene || !currentMovieResponse) return;
    if (!canAddTweenAtFrame(currentLayer.id, currentFrame, keyframes, tweens)) return;

    const layerKeyframes = keyframes[currentLayer.id] ?? [];

    // Find keyframe at or before current frame
    const startKeyframe = [...layerKeyframes]
      .filter(kf => kf.frame <= currentFrame)
      .sort((a, b) => b.frame - a.frame)[0];

    // Find keyframe after current frame
    const endKeyframe = [...layerKeyframes]
      .filter(kf => kf.frame > currentFrame)
      .sort((a, b) => a.frame - b.frame)[0];

    if (!startKeyframe || !endKeyframe) {
      console.warn('Need two keyframes to create a tween');
      return;
    }

    const layerTweens = tweens[currentLayer.id] ?? [];
    if (layerTweens.some(
      (tw) => tw.startFrame === startKeyframe.frame && tw.endFrame === endKeyframe.frame,
    )) {
      return;
    }

    const layerIdNum = parseInt(currentLayer.id);
    const startKeyframeId = parseInt(startKeyframe.id);
    const endKeyframeId = parseInt(endKeyframe.id);

    if (isNaN(layerIdNum) || isNaN(startKeyframeId) || isNaN(endKeyframeId)) {
      console.warn('Invalid layer or keyframe IDs');
      return;
    }

    try {
      // Save to server
      const tweenResponse = await tweenSave({
        id: 0,
        movieId: currentMovieResponse.id,
        sceneId: parseInt(currentScene.id),
        layerId: layerIdNum,
        startKeyFrameId: startKeyframeId,
        endKeyFrameId: endKeyframeId,
        easing: 'linear',
        name: '',
        description: '',
      });

      // Add to local store
      const newTween: Tween = {
        id: String(tweenResponse.id),
        layerId: currentLayer.id,
        startFrame: startKeyframe.frame,
        endFrame: endKeyframe.frame,
        easing: 'linear',
      };

      addTween(newTween);
    } catch (error) {
      console.error('Failed to create tween:', error);
    }
  };

  const deleteConnectedTweensForKeyframe = async (layerId: string, frame: number) => {
    const connectedTweens = (tweens[layerId] ?? []).filter(
      (tw) => tw.startFrame === frame || tw.endFrame === frame,
    );
    if (connectedTweens.length === 0) return;

    if (!currentScene || !currentMovieResponse) {
      connectedTweens.forEach((tw) => deleteTween(layerId, tw.startFrame));
      setSelectedTween(null);
      return;
    }

    const layerIdNum = parseInt(layerId);
    if (isNaN(layerIdNum)) {
      connectedTweens.forEach((tw) => deleteTween(layerId, tw.startFrame));
      setSelectedTween(null);
      return;
    }

    const layerKeyframes = keyframes[layerId] ?? [];

    for (const tween of connectedTweens) {
      const tweenId = parseInt(tween.id);
      const startKf = layerKeyframes.find((kf) => kf.frame === tween.startFrame);
      const endKf = layerKeyframes.find((kf) => kf.frame === tween.endFrame);

      if (
        !isNaN(tweenId) &&
        startKf &&
        endKf &&
        !isNaN(parseInt(startKf.id)) &&
        !isNaN(parseInt(endKf.id))
      ) {
        await tweenDelete({
          id: tweenId,
          movieId: currentMovieResponse.id,
          sceneId: parseInt(currentScene.id),
          layerId: layerIdNum,
          startKeyFrameId: parseInt(startKf.id),
          endKeyFrameId: parseInt(endKf.id),
          easing: tween.easing,
          name: '',
          description: '',
        });
      }

      deleteTween(layerId, tween.startFrame);
    }

    setSelectedTween(null);
  };

  const handleDeleteKeyframe = async () => {
    if (!selectedKeyframe || isDeleting) return;

    const { layerId, frame } = selectedKeyframe;
    const keyframe = keyframes[layerId]?.find((kf) => kf.frame === frame);
    if (!keyframe) return;

    setIsDeleting(true);
    try {
      if (!currentScene || !currentMovieResponse) {
        await deleteConnectedTweensForKeyframe(layerId, frame);
        deleteKeyframe(layerId, frame);
        setSelectedKeyframe(null);
        return;
      }

      const keyframeId = parseInt(keyframe.id);
      const layerIdNum = parseInt(layerId);
      if (isNaN(keyframeId) || isNaN(layerIdNum)) {
        await deleteConnectedTweensForKeyframe(layerId, frame);
        deleteKeyframe(layerId, frame);
        setSelectedKeyframe(null);
        return;
      }

      await deleteConnectedTweensForKeyframe(layerId, frame);
      await keyFrameDelete({
        id: keyframeId,
        movieId: currentMovieResponse.id,
        sceneId: parseInt(currentScene.id),
        layerId: layerIdNum,
        frameNumber: frame,
        objectStates: [],
      });
      deleteKeyframe(layerId, frame);
      setSelectedKeyframe(null);
    } catch (error) {
      console.error('Failed to delete keyframe:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteTween = async () => {
    if (!selectedTween || isDeleting) return;

    const { layerId, startFrame } = selectedTween;
    const tween = (tweens[layerId] ?? []).find((tw) => tw.startFrame === startFrame);
    if (!tween) return;

    setIsDeleting(true);
    try {
      if (!currentScene || !currentMovieResponse) {
        deleteTween(layerId, tween.startFrame);
        setSelectedTween(null);
        return;
      }

      const tweenId = parseInt(tween.id);
      const layerIdNum = parseInt(layerId);
      if (isNaN(tweenId) || isNaN(layerIdNum)) {
        deleteTween(layerId, tween.startFrame);
        setSelectedTween(null);
        return;
      }

      const layerKeyframes = keyframes[layerId] ?? [];
      const startKf = layerKeyframes.find((kf) => kf.frame === tween.startFrame);
      const endKf = layerKeyframes.find((kf) => kf.frame === tween.endFrame);

      if (!startKf || !endKf) {
        deleteTween(layerId, tween.startFrame);
        setSelectedTween(null);
        return;
      }

      await tweenDelete({
        id: tweenId,
        movieId: currentMovieResponse.id,
        sceneId: parseInt(currentScene.id),
        layerId: layerIdNum,
        startKeyFrameId: parseInt(startKf.id),
        endKeyFrameId: parseInt(endKf.id),
        easing: tween.easing,
        name: '',
        description: '',
      });

      deleteTween(layerId, tween.startFrame);
      setSelectedTween(null);
    } catch (error) {
      console.error('Failed to delete tween:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteKeyframeRef = useRef(handleDeleteKeyframe);
  const handleDeleteTweenRef = useRef(handleDeleteTween);
  handleDeleteKeyframeRef.current = handleDeleteKeyframe;
  handleDeleteTweenRef.current = handleDeleteTween;

  useEffect(() => {
    const handleDeleteKey = (e: KeyboardEvent) => {
      if (e.key !== 'Delete') return;

      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (
          target.isContentEditable ||
          tag === 'INPUT' ||
          tag === 'TEXTAREA' ||
          tag === 'SELECT'
        ) {
          return;
        }
      }

      const { selectedKeyframe, selectedTween } = useTimelineStore.getState();
      if (!selectedKeyframe && !selectedTween) return;

      e.preventDefault();
      if (selectedTween) {
        void handleDeleteTweenRef.current();
      } else if (selectedKeyframe) {
        void handleDeleteKeyframeRef.current();
      }
    };

    window.addEventListener('keydown', handleDeleteKey);
    return () => window.removeEventListener('keydown', handleDeleteKey);
  }, []);

  return (
    <div className="timeline-header">
      <div className="timeline-controls">
        <Tooltip content={t('timeline.prevFrame')}>
          <IconButton onClick={onPrevFrame} variant="ghost" size="3">
            <ArrowLeftIcon className="timeline-control-icon" aria-hidden="true" />
          </IconButton>
        </Tooltip>
        {isPlaying ? (
          <Tooltip content={t('timeline.pause')}>
            <IconButton onClick={onPause} variant="ghost" size="3">
              <PauseIcon className="timeline-control-icon" aria-hidden="true" />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip
            content={
              canPlay
                ? t('timeline.play')
                : t('timeline.playNeedsKeyframe')
            }
          >
            <IconButton
              onClick={onPlay}
              variant="ghost"
              size="3"
              disabled={!canPlay}
            >
              <PlayIcon className="timeline-control-icon" aria-hidden="true" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip content={t('timeline.stop')}>
          <IconButton onClick={onStop} variant="ghost" size="3">
            <StopIcon className="timeline-control-icon" aria-hidden="true" />
          </IconButton>
        </Tooltip>
        <Tooltip content={t('timeline.nextFrame')}>
          <IconButton onClick={onNextFrame} variant="ghost" size="3">
            <ArrowRightIcon className="timeline-control-icon" aria-hidden="true" />
          </IconButton>
        </Tooltip>
      </div>
      <div className="timeline-actions flex items-center gap-2">
        <Tooltip content={
          canAddKeyframe
            ? t('timeline.addKeyframe')
            : selectedKeyframe
              ? t('timeline.deselectKeyframe')
              : t('timeline.keyframeExists')
        }>
          <IconButton
            variant="outline"
            size="3"
            onClick={handleAddKeyframe}
            disabled={!canAddKeyframe || isCreatingKeyframe || isDeleting}
          >
            <PlusCircledIcon className="timeline-control-icon" aria-hidden="true" />
          </IconButton>
        </Tooltip>
        <Tooltip content={t('timeline.deleteKeyframe')}>
          <IconButton
            variant="outline"
            size="3"
            color="red"
            onClick={handleDeleteKeyframe}
            disabled={!selectedKeyframe || isDeleting || isCreatingKeyframe}
          >
            <MinusCircledIcon className="timeline-control-icon" aria-hidden="true" />
          </IconButton>
        </Tooltip>
        <Tooltip content={canAddTween ? t('timeline.addTween') : t('timeline.addTweenHint')}>
          <IconButton
            variant="outline"
            size="3"
            onClick={handleAddTween}
            disabled={!canAddTween || isDeleting || isCreatingKeyframe}
          >
            <ArrowRightIcon className="timeline-control-icon" aria-hidden="true" />
          </IconButton>
        </Tooltip>
        <Tooltip content={t('timeline.deleteTween')}>
          <IconButton
            variant="outline"
            size="3"
            color="red"
            onClick={handleDeleteTween}
            disabled={!selectedTween || isDeleting || isCreatingKeyframe}
          >
            <MinusCircledIcon className="timeline-control-icon" aria-hidden="true" />
          </IconButton>
        </Tooltip>
      </div>
      <div className="timeline-info">
        <div className='flex'>
          <label>{t('timeline.frame')}</label><span>{currentFrame}</span>
        </div>
        <div className='flex'>
          <label>{t('timeline.time')}</label><span>{time}s</span>
        </div>
        <div className='flex items-center'>
          <label>{t('timeline.total')}</label>
          <input
            type="number"
            className="timeline-total-input"
            value={totalFrames}
            onChange={(e) => onTotalFramesChange(parseInt(e.target.value) || 240)}
            min={1}
            max={9999}
            aria-label={t('timeline.totalFramesAria')}
          />
        </div>
      </div>

    </div>
  );
}

function TimelineLayers() {
  const { t } = useTranslation();
  const { layers, keyframes, tweens, addLayer, deleteLayer, toggleLayerVisibility, toggleLayerLock, reorderLayers, updateLayer } = useTimelineStore();
  const { currentScene, currentMovieResponse, currentLayer, setCurrentLayer } = useMovieStore();
  const clearSelection = useSelectionStore((state) => state.clearSelection);
  const { timelineLayerCreate, timelineLayerDelete, timelineLayerSave, timelineLayerSaveBatch } = useSignalR();
  const [draggedLayerId, setDraggedLayerId] = useState<string | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [isCreatingLayer, setIsCreatingLayer] = useState(false);
  const [deletingLayerIds, setDeletingLayerIds] = useState<Set<string>>(new Set());
  const [editingLayerId, setEditingLayerId] = useState<string | null>(null);
  const [lastLayerWarningOpen, setLastLayerWarningOpen] = useState(false);
  const [nonEmptyLayerWarningOpen, setNonEmptyLayerWarningOpen] = useState(false);

  // Memoize displayLayers to prevent unnecessary re-renders
  const sortedTimelineLayers = useMemo(() => sortTimelineLayers(layers), [layers]);
  const objectFallbackLayers = useMemo(
    () =>
      (currentScene?.objects ?? []).map((obj, i) => ({
        id: obj.id,
        objectId: obj.id,
        name: obj.name,
        order: i,
        visible: obj.visible,
        locked: obj.locked,
      })),
    [currentScene?.objects],
  );
  const displayLayers: TimelineLayer[] =
    layers.length > 0 ? sortedTimelineLayers : objectFallbackLayers;

  const handleAddLayer = async () => {
    if (!currentScene || isCreatingLayer) {
      console.warn('Cannot add layer: No scene selected');
      return;
    }

    const movieResponse = useMovieStore.getState().currentMovieResponse;
    if (!movieResponse) {
      console.warn('Cannot add layer: No movie selected');
      return;
    }

    // Prefer live store snapshot; fall back to a minimal SceneResponse from current scene
    const sceneResponse: SceneResponse =
      movieResponse.scenes.find((s) => String(s.id) === currentScene.id) ??
      {
        id: Number(currentScene.id),
        movieId: movieResponse.id,
        name: currentScene.name,
        description: '',
        framesTotal: 100,
        sorter: currentScene.order,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

    if (!Number.isFinite(sceneResponse.id) || sceneResponse.id <= 0) {
      console.warn('Cannot add layer: Invalid scene id', currentScene.id);
      return;
    }

    setIsCreatingLayer(true);
    try {
      const currentLayers = useTimelineStore.getState().layers;
      const layerCount = currentLayers.length > 0 ? currentLayers.length : displayLayers.length;
      const layerName = `Layer ${layerCount + 1}`;
      const sorter = layerCount;
      
      // Create layer on server via SignalR
      const createdLayer = await timelineLayerCreate(layerName, sceneResponse, sorter);
      
      // Convert TimelineLayerResponse to TimelineLayer and add to store
      const newLayer: TimelineLayer = {
        id: String(createdLayer.id),
        objectId: createdLayer.sceneObjects.length > 0 ? String(createdLayer.sceneObjects[0].id) : '',
        name: createdLayer.name,
        order: createdLayer.sorter,
        visible: true,
        locked: false,
      };
      
      addLayer(newLayer);
      setCurrentLayer(newLayer);
    } catch (error) {
      console.error('Failed to create timeline layer:', error);
      // Optionally show user-friendly error message
    } finally {
      setIsCreatingLayer(false);
    }
  };

  const handleDragStart = (e: React.DragEvent, layerId: string) => {
    setDraggedLayerId(layerId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', layerId);
    // Add a slight delay to allow drag image to be set
    setTimeout(() => {
      if (e.dataTransfer) {
        e.dataTransfer.setDragImage(new Image(), 0, 0);
      }
    }, 0);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = async (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    setDragOverIndex(null);
    
    if (!draggedLayerId || !currentMovieResponse) return;

    const currentIndex = displayLayers.findIndex(l => l.id.toString() === draggedLayerId);
    if (currentIndex === -1 || currentIndex === dropIndex) {
      setDraggedLayerId(null);
      return;
    }

    // Create new ordered list
    const newLayers = [...displayLayers];
    const [removed] = newLayers.splice(currentIndex, 1);
    newLayers.splice(dropIndex, 0, removed);

    // Update order values locally first
    const layerIds = newLayers.map(l => l.id.toString());
    reorderLayers(layerIds);

    // Update sorter values and save to server
    try {
      const layersToUpdate = newLayers.map((layer, index) => {
        const layerIdNum = parseInt(layer.id);
        if (isNaN(layerIdNum)) {
          // Skip layers that don't have numeric IDs (not yet saved to server)
          return null;
        }
        return {
          id: layerIdNum,
          movieId: currentMovieResponse.id,
          sceneId: parseInt(currentScene?.id || '0'),
          name: layer.name,
          sorter: index,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          sceneObjects: [],
          keyFrames: [],
          tweens: [],
        };
      }).filter((l): l is NonNullable<typeof l> => l !== null);

      if (layersToUpdate.length > 0) {
        await timelineLayerSaveBatch(layersToUpdate);
      }

      // Update local store with new order values
      newLayers.forEach((layer, index) => {
        updateLayer(layer.id, { order: index });
      });
    } catch (error) {
      console.error('Failed to reorder layers:', error);
      // Revert local changes on error
      reorderLayers(displayLayers.map(l => l.id.toString()));
    }
    
    setDraggedLayerId(null);
  };

  const handleRenameLayer = async (layerId: string, newName: string) => {
    setEditingLayerId(null);
    if (!newName.trim() || !currentMovieResponse) return;

    // Update local store
    updateLayer(layerId, { name: newName });

    // Save to server
    const layerIdNum = parseInt(layerId);
    if (!isNaN(layerIdNum)) {
      const layer = displayLayers.find(l => l.id.toString() === layerId);
      if (layer) {
        try {
          await timelineLayerSave({
            id: layerIdNum,
            movieId: currentMovieResponse.id,
            sceneId: parseInt(currentScene?.id || '0'),
            name: newName,
            sorter: layer.order,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            sceneObjects: [],
            keyFrames: [],
            tweens: [],
          });
        } catch (error) {
          console.error('Failed to save layer name:', error);
        }
      }
    }
  };

  const handleDeleteLayer = async (layerId: string) => {
    if (!currentMovieResponse) {
      console.warn('Cannot delete layer: No movie selected');
      return;
    }

    const layer = displayLayers.find(l => l.id.toString() === layerId);
    if (!layer) return;

    if (displayLayers.length <= 1) {
      setLastLayerWarningOpen(true);
      return;
    }

    const layerKeyframes = keyframes[layerId] ?? [];
    const layerTweens = tweens[layerId] ?? [];
    const layerIdNum = Number.parseInt(layerId, 10);
    const hasSceneObjects = (currentScene?.objects ?? []).some((obj) => {
      if (!Number.isNaN(layerIdNum) && obj.layerId === layerIdNum) {
        return true;
      }
      if (String(obj.layerId ?? '') === layerId) {
        return true;
      }
      return obj.id === layer.objectId || obj.id === layerId;
    });

    if (layerKeyframes.length > 0 || layerTweens.length > 0 || hasSceneObjects) {
      setNonEmptyLayerWarningOpen(true);
      return;
    }

    const parsedLayerId = parseInt(layerId);
    if (isNaN(parsedLayerId)) {
      // Layer not yet saved to server, just remove from local store
      deleteLayer(layerId);
      if (String(currentLayer?.id) === layerId) {
        const remaining = displayLayers.filter((l) => String(l.id) !== layerId);
        setCurrentLayer(remaining[0] ?? null);
      }
      return;
    }

    setDeletingLayerIds(prev => new Set(prev).add(layerId));
    try {
      // Create TimelineLayerResponse for deletion
      const layerResponse = {
        id: parseInt(layerId),
        movieId: currentMovieResponse.id,
        sceneId: parseInt(currentScene?.id || '0'),
        name: layer.name,
        sorter: layer.order,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        sceneObjects: [],
        keyFrames: [],
        tweens: [],
      };

      await timelineLayerDelete(layerResponse);
      deleteLayer(layerId);
      if (String(currentLayer?.id) === layerId) {
        const remaining = displayLayers.filter((l) => String(l.id) !== layerId);
        setCurrentLayer(remaining[0] ?? null);
      }
    } catch (error) {
      console.error('Failed to delete layer:', error);
    } finally {
      setDeletingLayerIds(prev => {
        const next = new Set(prev);
        next.delete(layerId);
        return next;
      });
    }
  };

  const handleDragEnd = () => {
    setDraggedLayerId(null);
    setDragOverIndex(null);
  };

  return (
    <div className='timeline-layers-container flex flex-col'>
      <div className="timeline-layers-header flex w-full">
      <Button
        variant="ghost"
        size="1"
        className="flex items-center gap-2 h-10"
        onClick={handleAddLayer}
        disabled={isCreatingLayer || !currentScene || !currentMovieResponse}
        title={t('timeline.addLayer')}
      >
        <LayersIcon className="" aria-hidden="true" />
        <PlusIcon className="" aria-hidden="true" />
{isCreatingLayer ? '...' : t('timeline.newLayer')}
      </Button>
        </div>
      <div className="timeline-layers">
        {displayLayers.map((layer, index) => (
          <div
            key={layer.id}
            className={`timeline-layer ${currentLayer?.id === layer.id ? 'selected' : ''} ${draggedLayerId === layer.id.toString() ? 'dragging' : ''} ${dragOverIndex === index ? 'drag-over' : ''}`}
            onClick={() => {
              clearSelection();
              setCurrentLayer(layer);
            }}
            draggable={true}
            onDragStart={(e) => handleDragStart(e, layer.id.toString())}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, index)}
            onDragEnd={handleDragEnd}
          >
            <span className="timeline-layer-drag-handle" title={t('timeline.dragReorder')}>☰</span>
            <button
              onClick={(e) => { e.stopPropagation(); toggleLayerVisibility(layer.id); }}
              className={layer.visible ? 'visible' : 'hidden'}
              title={layer.visible ? t('common.hide') : t('common.show')}
            >
              👁
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); toggleLayerLock(layer.id); }}
              className={layer.locked ? 'visible' : 'hidden'}
              title={layer.locked ? t('common.unlock') : t('common.lock')}
            >
              🔒
            </button>
            {editingLayerId === layer.id ? (
              <input
                type="text"
                className="timeline-layer-name-input"
                defaultValue={layer.name}
                autoFocus
                onBlur={(e) => handleRenameLayer(layer.id, e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleRenameLayer(layer.id, e.currentTarget.value);
                  if (e.key === 'Escape') setEditingLayerId(null);
                }}
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <span
                className="timeline-layer-name"
                onDoubleClick={(e) => { e.stopPropagation(); setEditingLayerId(layer.id); }}
              >
                {layer.name}
              </span>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); handleDeleteLayer(layer.id.toString()); }}
              className="timeline-layer-delete"
              disabled={deletingLayerIds.has(layer.id.toString())}
              title={t('timeline.deleteLayer')}
            >
              {deletingLayerIds.has(layer.id.toString()) ? '...' : '×'}
            </button>
          </div>
        ))}
      </div>
      <ConfirmModal
        open={lastLayerWarningOpen}
        onOpenChange={setLastLayerWarningOpen}
        title={t('timeline.cannotDeleteTitle')}
        description={t('timeline.cannotDeleteLast')}
        confirmText={t('common.ok')}
        cancelText={t('common.close')}
        onConfirm={() => setLastLayerWarningOpen(false)}
      />
      <ConfirmModal
        open={nonEmptyLayerWarningOpen}
        onOpenChange={setNonEmptyLayerWarningOpen}
        title={t('timeline.cannotDeleteTitle')}
        description={t('timeline.cannotDeleteNonEmpty')}
        confirmText={t('common.ok')}
        cancelText={t('common.close')}
        onConfirm={() => setNonEmptyLayerWarningOpen(false)}
      />
    </div>
  );
}

interface TimelineFramesProps {
  currentFrame: number;
  totalFrames: number;
  onFrameClick: (frame: number) => void;
}

function TimelineFrames({ currentFrame, totalFrames, onFrameClick }: TimelineFramesProps) {
  const { t } = useTranslation();
  const framesRef = useRef<HTMLDivElement>(null);
  const didPanRef = useRef(false);
  const panStartRef = useRef<{
    scrollLeft: number;
    clientX: number;
  } | null>(null);
  const {
    keyframes,
    tweens,
    addKeyframe,
    deleteKeyframe,
    updateTween,
    deleteTween,
    deleteAllKeyframes,
    deleteAllTweens,
    addTween,
    layers,
    selectedKeyframe,
    selectedTween,
    setSelectedKeyframe,
    setSelectedTween,
  } = useTimelineStore();
  const { currentScene, batchUpdateSceneObjects, currentMovieResponse, currentLayer, setCurrentLayer } = useMovieStore();
  const clearSelection = useSelectionStore((state) => state.clearSelection);
  const { fps } = useEditorStore();
  const { keyFrameSave, keyFrameDelete, tweenSave, tweenDelete } = useSignalR();
  const [hoveredFrame, setHoveredFrame] = useState<number | null>(null);
  const [selectedFrame, setSelectedFrame] = useState<number | null>(null);
  const [selectedLayerId, setSelectedLayerId] = useState<string | null>(null);
  const [isPanning, setIsPanning] = useState(false);
  const [deletingKeyframes, setDeletingKeyframes] = useState<Set<string>>(new Set());
  const [draggingKeyframe, setDraggingKeyframe] = useState<{
    layerId: string;
    frame: number;
    newFrame: number;
    affectedTweens: { tweenId: string; isStart: boolean; isEnd: boolean }[];
  } | null>(null);
  const draggingRef = useRef<{
    layerId: string;
    frame: number;
    newFrame: number;
    affectedTweens: { tweenId: string; isStart: boolean; isEnd: boolean }[];
  } | null>(null);
  // Yalnızca sahne değişince timeline seçimini sıfırla. currentLayer değişiminde çalışma:
  // aksi halde başka layer track'ine tıklanınca setCurrentLayer sonrası selectedFrame / selectedLayerId temizlenir
  // ve yeşil seçim (track + frame göstergesi) görünmez.
  useEffect(() => {
    setSelectedKeyframe(null);
    setSelectedTween(null);
    setSelectedFrame(null);
    setSelectedLayerId(null);
    setHoveredFrame(null);
    draggingRef.current = null;
    setDraggingKeyframe(null);
  }, [currentScene?.id]);

  // Memoize displayLayers to prevent unnecessary re-renders (same as TimelineLayers)
  const sortedTimelineLayers = useMemo(() => sortTimelineLayers(layers), [layers]);
  const objectFallbackLayers = useMemo(
    () =>
      (currentScene?.objects ?? []).map((obj, i) => ({
        id: obj.id,
        objectId: obj.id,
        name: obj.name,
        order: i,
        visible: obj.visible,
        locked: obj.locked,
      })),
    [currentScene?.objects],
  );
  const displayLayers: TimelineLayer[] =
    layers.length > 0 ? sortedTimelineLayers : objectFallbackLayers;

  const selectedTrackIndex = useMemo(() => {
    if (!selectedLayerId) return null;
    const idx = displayLayers.findIndex((l) => String(l.id) === selectedLayerId);
    return idx >= 0 ? idx : null;
  }, [displayLayers, selectedLayerId]);

  useEffect(() => {
    if (selectedLayerId === null) return;
    const stillExists = displayLayers.some((l) => String(l.id) === selectedLayerId);
    if (!stillExists) {
      setSelectedLayerId(null);
      setSelectedFrame(null);
    }
  }, [displayLayers, selectedLayerId]);

  const handleRulerMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const frame = Math.floor(x / FRAME_WIDTH);
    const clickedFrame = Math.max(0, Math.min(frame, totalFrames - 1));
    setSelectedTween(null);
    setSelectedKeyframe(null);
    setSelectedFrame(clickedFrame);
    setSelectedLayerId(null);
    onFrameClick(clickedFrame);
  };

  const handleKeyframeMouseDown = (e: React.MouseEvent, layerId: string, frame: number) => {
    e.stopPropagation();
    if (e.button !== 0) return;

    const trackIndex = displayLayers.findIndex((l) => String(l.id) === layerId);
    if (trackIndex >= 0) {
      setSelectedLayerId(layerId);
      const layer = displayLayers[trackIndex];
      if (layer) {
        clearSelection();
        setCurrentLayer(layer);
      }
    }

    setSelectedKeyframe({ layerId, frame });
    setSelectedTween(null);
    setSelectedFrame(frame);
    onFrameClick(frame);

    const layerTweens = tweens[layerId] ?? [];
    const affectedTweens = layerTweens
      .filter(tw => tw.startFrame === frame || tw.endFrame === frame)
      .map(tw => ({
        tweenId: tw.id,
        isStart: tw.startFrame === frame,
        isEnd: tw.endFrame === frame,
      }));

    const startClientX = e.clientX;
    let dragStarted = false;
    const initialDrag = { layerId, frame, newFrame: frame, affectedTweens };
    const trackElement = e.currentTarget.closest('.timeline-track');

    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (!trackElement) return;

      if (!dragStarted) {
        if (Math.abs(moveEvent.clientX - startClientX) <= DRAG_THRESHOLD) return;
        dragStarted = true;
        draggingRef.current = initialDrag;
        setDraggingKeyframe(initialDrag);
      }

      if (!draggingRef.current) return;

      const rect = trackElement.getBoundingClientRect();
      const x = moveEvent.clientX - rect.left;
      let newFrame = Math.max(0, Math.min(Math.floor(x / FRAME_WIDTH), totalFrames - 1));

      const layerKeyframes = keyframes[layerId] ?? [];
      const otherKeyframes = layerKeyframes.filter(kf => kf.frame !== frame);

      const leftKeyframes = otherKeyframes.filter(kf => kf.frame < frame).sort((a, b) => b.frame - a.frame);
      const rightKeyframes = otherKeyframes.filter(kf => kf.frame > frame).sort((a, b) => a.frame - b.frame);

      const leftBound = leftKeyframes.length > 0 ? leftKeyframes[0].frame + 1 : 0;
      const rightBound = rightKeyframes.length > 0 ? rightKeyframes[0].frame - 1 : totalFrames - 1;

      newFrame = Math.max(leftBound, Math.min(newFrame, rightBound));

      const updated = { ...draggingRef.current, newFrame };
      draggingRef.current = updated;
      setDraggingKeyframe(updated);
    };
    
    const handleMouseUp = async () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);

      if (!dragStarted) return;

      const currentDrag = draggingRef.current;
      draggingRef.current = null;
      setDraggingKeyframe(null);

      if (!currentDrag || !currentScene || !currentMovieResponse) return;

      const { layerId: dragLayerId, frame: oldFrame, newFrame, affectedTweens } = currentDrag;

      if (oldFrame !== newFrame) {
        const keyframe = keyframes[dragLayerId]?.find(kf => kf.frame === oldFrame);
        if (keyframe) {
          const keyframeId = parseInt(keyframe.id);
          const layerIdNum = parseInt(dragLayerId);
          if (!isNaN(keyframeId) && !isNaN(layerIdNum)) {
            try {
              const existingAtNewFrame = keyframes[dragLayerId]?.find(kf => kf.frame === newFrame);
              if (existingAtNewFrame) {
                console.warn('Keyframe already exists at target frame');
                return;
              }

              // Save keyframe with new position
              const keyFrameRequest = {
                id: keyframeId,
                movieId: currentMovieResponse.id,
                sceneId: parseInt(currentScene.id),
                layerId: layerIdNum,
                frameNumber: newFrame,
                objectStates: [],
              };

              await keyFrameSave(keyFrameRequest);

              // Update local keyframe
              deleteKeyframe(dragLayerId, oldFrame);
              addKeyframe({
                ...keyframe,
                frame: newFrame,
              });

              // Update affected tweens
              const layerTweens = tweens[dragLayerId] ?? [];
              const layerKeyframes = keyframes[dragLayerId] ?? [];

              for (const affectedTween of affectedTweens) {
                const tween = layerTweens.find(tw => tw.id === affectedTween.tweenId);
                if (!tween) continue;

                const tweenId = parseInt(tween.id);
                if (isNaN(tweenId)) continue;

                // Calculate new start/end frames
                const newStartFrame = affectedTween.isStart ? newFrame : tween.startFrame;
                const newEndFrame = affectedTween.isEnd ? newFrame : tween.endFrame;

                // Find the keyframe IDs
                let startKeyframeId: number;
                let endKeyframeId: number;

                if (affectedTween.isStart) {
                  startKeyframeId = keyframeId; // The dragged keyframe
                  const endKf = layerKeyframes.find(kf => kf.frame === tween.endFrame);
                  endKeyframeId = endKf ? parseInt(endKf.id) : 0;
                } else {
                  const startKf = layerKeyframes.find(kf => kf.frame === tween.startFrame);
                  startKeyframeId = startKf ? parseInt(startKf.id) : 0;
                  endKeyframeId = keyframeId; // The dragged keyframe
                }

                if (isNaN(startKeyframeId) || isNaN(endKeyframeId)) continue;

                // Save updated tween to server
                await tweenSave({
                  id: tweenId,
                  movieId: currentMovieResponse.id,
                  sceneId: parseInt(currentScene.id),
                  layerId: layerIdNum,
                  startKeyFrameId: startKeyframeId,
                  endKeyFrameId: endKeyframeId,
                  easing: tween.easing,
                  name: '',
                  description: '',
                });

                // Update local tween
                updateTween(tween.id, {
                  startFrame: newStartFrame,
                  endFrame: newEndFrame,
                });
              }

              setSelectedTween(null);
              setSelectedKeyframe({ layerId: dragLayerId, frame: newFrame });
              setSelectedFrame(newFrame);
              onFrameClick(newFrame);
            } catch (error) {
              console.error('Failed to move keyframe:', error);
            }
          }
        }
      }
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  const deleteConnectedTweensForKeyframe = async (layerId: string, frame: number) => {
    const connectedTweens = (tweens[layerId] ?? []).filter(
      (tw) => tw.startFrame === frame || tw.endFrame === frame
    );
    if (connectedTweens.length === 0) return;

    if (!currentScene || !currentMovieResponse) {
      connectedTweens.forEach((tw) => deleteTween(layerId, tw.startFrame));
      setSelectedTween(null);
      return;
    }

    const layerIdNum = parseInt(layerId);
    if (isNaN(layerIdNum)) {
      connectedTweens.forEach((tw) => deleteTween(layerId, tw.startFrame));
      setSelectedTween(null);
      return;
    }

    const layerKeyframes = keyframes[layerId] ?? [];

    for (const tween of connectedTweens) {
      const tweenId = parseInt(tween.id);
      const startKf = layerKeyframes.find((kf) => kf.frame === tween.startFrame);
      const endKf = layerKeyframes.find((kf) => kf.frame === tween.endFrame);

      if (
        !isNaN(tweenId) &&
        startKf &&
        endKf &&
        !isNaN(parseInt(startKf.id)) &&
        !isNaN(parseInt(endKf.id))
      ) {
        await tweenDelete({
          id: tweenId,
          movieId: currentMovieResponse.id,
          sceneId: parseInt(currentScene.id),
          layerId: layerIdNum,
          startKeyFrameId: parseInt(startKf.id),
          endKeyFrameId: parseInt(endKf.id),
          easing: tween.easing,
          name: '',
          description: '',
        });
      }

      deleteTween(layerId, tween.startFrame);
    }

    setSelectedTween(null);
  };

  const handleKeyframeDoubleClick = async (e: React.MouseEvent, layerId: string, frame: number) => {
    e.stopPropagation();
    
    if (!currentScene || !currentMovieResponse) {
      await deleteConnectedTweensForKeyframe(layerId, frame);
      deleteKeyframe(layerId, frame);
      return;
    }

    const keyframe = keyframes[layerId]?.find(kf => kf.frame === frame);
    if (!keyframe) return;

    const keyframeId = parseInt(keyframe.id);
    if (isNaN(keyframeId)) {
      deleteKeyframe(layerId, frame);
      setSelectedKeyframe(null);
      return;
    }

    const layerIdNum = parseInt(layerId);
    if (isNaN(layerIdNum)) {
      deleteKeyframe(layerId, frame);
      setSelectedKeyframe(null);
      return;
    }

    setDeletingKeyframes(prev => new Set(prev).add(keyframe.id));
    try {
      await deleteConnectedTweensForKeyframe(layerId, frame);

      const keyFrameRequest = {
        id: keyframeId,
        movieId: currentMovieResponse.id,
        sceneId: parseInt(currentScene.id),
        layerId: layerIdNum,
        frameNumber: frame,
        objectStates: [],
      };
      await keyFrameDelete(keyFrameRequest);
      deleteKeyframe(layerId, frame);
      setSelectedKeyframe(null);
    } catch (error) {
      console.error('Failed to delete keyframe:', error);
    } finally {
      setDeletingKeyframes(prev => {
        const next = new Set(prev);
        next.delete(keyframe.id);
        return next;
      });
    }
  };

  const handleTweenMouseDown = (e: React.MouseEvent, layerId: string, tween: Tween) => {
    e.stopPropagation();
    if (e.button !== 0) return;

    // Tween bar sadece seçim için; taşıma uç keyframe'ler üzerinden yapılır.
    const layer = displayLayers.find(l => l.id.toString() === layerId);
    if (layer) {
      clearSelection();
      setCurrentLayer(layer);
    }
    setSelectedTween({ layerId, startFrame: tween.startFrame });
    setSelectedKeyframe(null);
    setSelectedFrame(null);
    setSelectedLayerId(null);
  };

  const handleTrackMouseDown = (e: React.MouseEvent, layer: TimelineLayer) => {
    if (e.button !== 0) return;
    if ((e.target as HTMLElement).closest('.timeline-keyframe, .timeline-tween')) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const frame = Math.floor(x / FRAME_WIDTH);
    const clickedFrame = Math.max(0, Math.min(frame, totalFrames - 1));

    setSelectedLayerId(String(layer.id));

    clearSelection();
    setCurrentLayer(layer);

    setSelectedTween(null);
    setSelectedKeyframe(null);
    setSelectedFrame(clickedFrame);
    onFrameClick(clickedFrame);
  };

  const handleTrackMouseMove = (e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const frame = Math.floor(x / FRAME_WIDTH);
    setHoveredFrame(Math.max(0, Math.min(frame, totalFrames - 1)));
  };

  const handleTrackMouseLeave = () => {
    setHoveredFrame(null);
  };

  const handleFramesMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return;

    const target = e.target as HTMLElement;
    if (target.closest('.timeline-keyframe, .timeline-tween, .timeline-cursor-head')) return;

    const container = framesRef.current;
    if (!container) return;

    didPanRef.current = false;
    panStartRef.current = {
      scrollLeft: container.scrollLeft,
      clientX: e.clientX,
    };

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const panStart = panStartRef.current;
      const panContainer = framesRef.current;
      if (!panStart || !panContainer) return;

      const dx = moveEvent.clientX - panStart.clientX;

      if (!didPanRef.current && Math.abs(dx) > DRAG_THRESHOLD) {
        didPanRef.current = true;
        setIsPanning(true);
      }

      if (didPanRef.current) {
        panContainer.scrollLeft = panStart.scrollLeft - dx;
      }
    };

    const handleMouseUp = () => {
      panStartRef.current = null;
      setIsPanning(false);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  // Context menu: add keyframe at a specific frame on a layer
  const handleContextAddKeyframe = async (layerId: string, frame: number) => {
    if (!currentScene || !currentMovieResponse) return;

    const layerIdNum = parseInt(layerId);
    if (isNaN(layerIdNum)) return;

    const existingKeyframe = keyframes[layerId]?.find(kf => kf.frame === frame);
    if (existingKeyframe) return;

    const layer = displayLayers.find((l) => l.id === layerId);
    if (!layer) return;

    try {
      const keyFrameRequest = {
        id: 0,
        movieId: currentMovieResponse.id,
        sceneId: parseInt(currentScene.id),
        layerId: layerIdNum,
        frameNumber: frame,
        objectStates: [],
      };
      const createdKeyframe = await keyFrameSave(keyFrameRequest);
      const layerKfs = keyframes[layerId] ?? [];
      const { state, objectStates } = seedNewKeyframeState(layer, frame, layerKfs, currentScene);
      addKeyframe({
        id: String(createdKeyframe.id),
        layerId,
        frame,
        state,
        ...(objectStates ? { objectStates } : {}),
      });
      setSelectedTween(null);
      setSelectedKeyframe({ layerId, frame });
      setSelectedFrame(frame);
      onFrameClick(frame);
    } catch (error) {
      console.error('Failed to create keyframe:', error);
    }
  };

  // Context menu: delete keyframe at a specific frame on a layer
  const handleContextDeleteKeyframe = async (layerId: string, frame: number) => {
    const keyframe = keyframes[layerId]?.find(kf => kf.frame === frame);
    if (!keyframe) return;

    if (!currentScene || !currentMovieResponse) {
      await deleteConnectedTweensForKeyframe(layerId, frame);
      deleteKeyframe(layerId, frame);
      return;
    }

    const keyframeId = parseInt(keyframe.id);
    const layerIdNum = parseInt(layerId);
    if (isNaN(keyframeId) || isNaN(layerIdNum)) {
      await deleteConnectedTweensForKeyframe(layerId, frame);
      deleteKeyframe(layerId, frame);
      return;
    }

    try {
      await deleteConnectedTweensForKeyframe(layerId, frame);

      await keyFrameDelete({
        id: keyframeId,
        movieId: currentMovieResponse.id,
        sceneId: parseInt(currentScene.id),
        layerId: layerIdNum,
        frameNumber: frame,
        objectStates: [],
      });
      deleteKeyframe(layerId, frame);
      setSelectedKeyframe(null);
    } catch (error) {
      console.error('Failed to delete keyframe:', error);
    }
  };

  // Context menu: add tween between surrounding keyframes
  const handleContextAddTween = async (layerId: string, frame: number) => {
    if (!currentScene || !currentMovieResponse) return;
    if (!canAddTweenAtFrame(layerId, frame, keyframes, tweens)) return;

    const layerKeyframes = keyframes[layerId] ?? [];

    const startKf = [...layerKeyframes]
      .filter(kf => kf.frame <= frame)
      .sort((a, b) => b.frame - a.frame)[0];

    const endKf = [...layerKeyframes]
      .filter(kf => kf.frame > frame)
      .sort((a, b) => a.frame - b.frame)[0];

    if (!startKf || !endKf) {
      console.warn('Need two keyframes to create a tween');
      return;
    }

    const layerIdNum = parseInt(layerId);
    const startKeyframeId = parseInt(startKf.id);
    const endKeyframeId = parseInt(endKf.id);

    if (isNaN(layerIdNum) || isNaN(startKeyframeId) || isNaN(endKeyframeId)) {
      console.warn('Invalid layer or keyframe IDs');
      return;
    }

    try {
      // Save to server
      const tweenResponse = await tweenSave({
        id: 0,
        movieId: currentMovieResponse.id,
        sceneId: parseInt(currentScene.id),
        layerId: layerIdNum,
        startKeyFrameId: startKeyframeId,
        endKeyFrameId: endKeyframeId,
        easing: 'linear',
        name: '',
        description: '',
      });

      // Add to local store
      addTween({
        id: String(tweenResponse.id),
        layerId,
        startFrame: startKf.frame,
        endFrame: endKf.frame,
        easing: 'linear',
      });
    } catch (error) {
      console.error('Failed to create tween:', error);
    }
  };

  // Context menu: delete tween at a specific frame on a layer
  const handleContextDeleteTween = async (layerId: string, frame: number) => {
    const layerTweens = tweens[layerId] ?? [];
    const tween = layerTweens.find(tw => tw.startFrame <= frame && tw.endFrame > frame);
    if (!tween) return;

    if (!currentScene || !currentMovieResponse) {
      deleteTween(layerId, tween.startFrame);
      setSelectedTween(null);
      return;
    }

    const tweenId = parseInt(tween.id);
    const layerIdNum = parseInt(layerId);

    if (isNaN(tweenId) || isNaN(layerIdNum)) {
      deleteTween(layerId, tween.startFrame);
      setSelectedTween(null);
      return;
    }

    try {
      // Find the keyframes for this tween
      const layerKeyframes = keyframes[layerId] ?? [];
      const startKf = layerKeyframes.find(kf => kf.frame === tween.startFrame);
      const endKf = layerKeyframes.find(kf => kf.frame === tween.endFrame);

      if (!startKf || !endKf) {
        deleteTween(layerId, tween.startFrame);
        setSelectedTween(null);
        return;
      }

      await tweenDelete({
        id: tweenId,
        movieId: currentMovieResponse.id,
        sceneId: parseInt(currentScene.id),
        layerId: layerIdNum,
        startKeyFrameId: parseInt(startKf.id),
        endKeyFrameId: parseInt(endKf.id),
        easing: tween.easing,
        name: '',
        description: '',
      });

      deleteTween(layerId, tween.startFrame);
      setSelectedTween(null);
    } catch (error) {
      console.error('Failed to delete tween:', error);
    }
  };

  // Context menu: delete all keyframes for a layer
  const handleClearAllKeyframes = async (layerId: string) => {
    const layerKeyframes = keyframes[layerId];
    if (!layerKeyframes || layerKeyframes.length === 0) return;

    if (!currentScene || !currentMovieResponse) {
      deleteAllKeyframes(layerId);
      return;
    }

    const layerIdNum = parseInt(layerId);
    if (isNaN(layerIdNum)) {
      deleteAllKeyframes(layerId);
      return;
    }

    // Delete all keyframes from server
    try {
      await Promise.all(
        layerKeyframes.map(async (kf) => {
          const keyframeId = parseInt(kf.id);
          if (!isNaN(keyframeId)) {
            await keyFrameDelete({
              id: keyframeId,
              movieId: currentMovieResponse.id,
              sceneId: parseInt(currentScene.id),
              layerId: layerIdNum,
              frameNumber: kf.frame,
              objectStates: [],
            });
          }
        })
      );
      deleteAllKeyframes(layerId);
      setSelectedKeyframe(null);
    } catch (error) {
      console.error('Failed to delete all keyframes:', error);
    }
  };

  // Context menu: delete all tweens for a layer
  const handleClearAllTweens = async (layerId: string) => {
    const layerTweens = tweens[layerId];
    if (!layerTweens || layerTweens.length === 0) return;

    if (!currentScene || !currentMovieResponse) {
      deleteAllTweens(layerId);
      return;
    }

    const layerIdNum = parseInt(layerId);
    if (isNaN(layerIdNum)) {
      deleteAllTweens(layerId);
      return;
    }

    const layerKeyframes = keyframes[layerId] ?? [];

    // Delete all tweens from server
    try {
      await Promise.all(
        layerTweens.map(async (tw) => {
          const tweenId = parseInt(tw.id);
          if (isNaN(tweenId)) return;

          const startKf = layerKeyframes.find(kf => kf.frame === tw.startFrame);
          const endKf = layerKeyframes.find(kf => kf.frame === tw.endFrame);

          if (!startKf || !endKf) return;

          await tweenDelete({
            id: tweenId,
            movieId: currentMovieResponse.id,
            sceneId: parseInt(currentScene.id),
            layerId: layerIdNum,
            startKeyFrameId: parseInt(startKf.id),
            endKeyFrameId: parseInt(endKf.id),
            easing: tw.easing,
            name: '',
            description: '',
          });
        })
      );
      deleteAllTweens(layerId);
      setSelectedTween(null);
    } catch (error) {
      console.error('Failed to delete all tweens:', error);
    }
  };

  // Helper: check if a keyframe exists at a given frame for a layer
  const hasKeyframeAt = (layerId: string, frame: number) =>
    (keyframes[layerId] ?? []).some(kf => kf.frame === frame);

  // Helper: check if a tween covers a given frame for a layer
  const hasTweenAt = (layerId: string, frame: number) =>
    (tweens[layerId] ?? []).some(tw => tw.startFrame <= frame && tw.endFrame > frame);

  // Helper: get frame number from a mouse event on a track
  const getFrameFromEvent = (e: React.MouseEvent) => {
    const trigger = e.currentTarget.closest('.timeline-track');
    if (!trigger) return 0;
    const rect = trigger.getBoundingClientRect();
    const x = e.clientX - rect.left;
    return Math.max(0, Math.min(Math.floor(x / FRAME_WIDTH), totalFrames - 1));
  };

  const handleTrackDoubleClick = async (e: React.MouseEvent, layerId: string) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const frame = Math.floor(x / FRAME_WIDTH);

    if (!currentScene || !currentMovieResponse) return;

    const layerIdNum = parseInt(layerId);
    if (isNaN(layerIdNum)) {
      console.warn('Cannot add keyframe: Invalid layer id', layerId);
      return;
    }

    // Check if keyframe already exists at this frame
    const existingKeyframe = keyframes[layerId]?.find(kf => kf.frame === frame);
    if (existingKeyframe) {
      return;
    }

    const layer = displayLayers.find((l) => l.id === layerId);
    if (!layer) {
      console.warn('Cannot add keyframe: layer not found', layerId);
      return;
    }

    try {
      const keyFrameRequest = {
        id: 0,
        movieId: currentMovieResponse.id,
        sceneId: parseInt(currentScene.id),
        layerId: layerIdNum,
        frameNumber: frame,
        objectStates: [],
      };
      
      const createdKeyframe = await keyFrameSave(keyFrameRequest);

      const layerKfs = keyframes[layerId] ?? [];
      const { state, objectStates } = seedNewKeyframeState(layer, frame, layerKfs, currentScene);

      const newKeyframe: Keyframe = {
        id: String(createdKeyframe.id),
        layerId,
        frame,
        state,
        ...(objectStates ? { objectStates } : {}),
      };

      addKeyframe(newKeyframe);
      setSelectedTween(null);
      setSelectedKeyframe({ layerId, frame });
      setSelectedFrame(frame);
      onFrameClick(frame);
    } catch (error) {
      console.error('Failed to create keyframe:', error);
    }
  };

  // Keep latest scene in a ref so pose-application doesn't re-run on every object drag/resize update.
  const currentSceneRef = useRef(currentScene);
  useEffect(() => {
    currentSceneRef.current = currentScene;
  }, [currentScene]);

  // Interpolate state at current frame (per-object, per-layer)
  useEffect(() => {
    const scene = currentSceneRef.current;
    if (!scene) return;

    const frameId = requestAnimationFrame(() => {
      const poseUpdates: Record<string, Partial<SceneObject>> = {};

      displayLayers.forEach(layer => {
        const layerKeyframes = keyframes[layer.id];
        const layerTweens = tweens[layer.id];

        // Objects in this layer (match by layerId, or by object id when fallback: one object per layer)
        const objectsInLayer = scene.objects.filter(
          obj => String(obj.layerId) === layer.id || obj.id === layer.id
        );

        if (objectsInLayer.length === 0) return;

        const before = [...(layerKeyframes ?? [])]
          .filter(kf => kf.frame <= currentFrame)
          .sort((a, b) => b.frame - a.frame)[0];

        const after = [...(layerKeyframes ?? [])]
          .filter(kf => kf.frame > currentFrame)
          .sort((a, b) => a.frame - b.frame)[0];

        const activeTween = layerTweens?.find(
          tw => tw.startFrame <= currentFrame && tw.endFrame > currentFrame
        );

        objectsInLayer.forEach(obj => {
          const restPose = sceneObjectRestPoseState(obj);

          const getStateForObject = (kf: Keyframe) => {
            const objStates = kf.objectStates;
            if (objStates && objStates[obj.id]) return objStates[obj.id];
            if (obj.id === layer.objectId) return kf.state;
            return null;
          };

          const beforeState = before ? getStateForObject(before) : null;
          const exactKeyframe = (layerKeyframes ?? []).find((kf) => kf.frame === currentFrame);
          const exactState = exactKeyframe ? getStateForObject(exactKeyframe) : null;

          // Resolution order:
          // 1) exact keyframe at cursor
          // 2) previous keyframe before cursor
          // 3) defaultState (defaultPose) — frame 0 ile ilk keyframe arası dahil
          let stateToApply = exactState ?? beforeState ?? restPose;

          if (
            activeTween &&
            before &&
            after &&
            before.frame === activeTween.startFrame &&
            after.frame === activeTween.endFrame
          ) {
            const tweenStartState = beforeState ?? restPose;
            const afterState = getStateForObject(after) ?? tweenStartState;
            if (after.frame > before.frame) {
              const progress = (currentFrame - before.frame) / (after.frame - before.frame);
              const eased = applyEasing(progress, activeTween.easing);
              stateToApply = {
                x: lerp(tweenStartState.x, afterState.x, eased),
                y: lerp(tweenStartState.y, afterState.y, eased),
                width: lerp(tweenStartState.width, afterState.width, eased),
                height: lerp(tweenStartState.height, afterState.height, eased),
                rotation: lerp(tweenStartState.rotation, afterState.rotation, eased),
                opacity: lerp(tweenStartState.opacity, afterState.opacity, eased),
              };
            }
          }

          poseUpdates[obj.id] = stateToApply;
        });
      });

      if (Object.keys(poseUpdates).length > 0) {
        batchUpdateSceneObjects(poseUpdates);
      }
    });

    return () => cancelAnimationFrame(frameId);
  }, [currentFrame, keyframes, tweens, displayLayers, batchUpdateSceneObjects]);

  // Calculate ruler marks - show seconds instead of frame numbers
  const framesPerSecond = fps;
  const secondsInterval = 1; // Show every second
  const framesPerMark = Math.max(1, Math.floor(framesPerSecond * secondsInterval));
  const rulerMarks = Array.from({ length: Math.ceil(totalFrames / framesPerMark) }, (_, i) => ({
    frame: i * framesPerMark,
    second: (i * framesPerMark / framesPerSecond).toFixed(1)
  }));

  return (
    <div
      className={`timeline-frames${isPanning ? ' panning' : ''}`}
      ref={framesRef}
      onMouseDown={handleFramesMouseDown}
    >
      <div 
        className="timeline-ruler" 
        onMouseDown={handleRulerMouseDown}
        style={{ 
          width: totalFrames * FRAME_WIDTH,
          '--frame-width': `${FRAME_WIDTH}px`
        } as React.CSSProperties}
      >
        {rulerMarks.map((mark) => (
          <span
            key={mark.frame}
            className="timeline-ruler-mark"
            style={{ '--ruler-left': `${mark.frame * FRAME_WIDTH}px` } as React.CSSProperties}
          >
            {mark.second}s
          </span>
        ))}
        <div 
          className="timeline-cursor-head"
          style={{ '--cursor-left': `${currentFrame * FRAME_WIDTH}px` } as React.CSSProperties}
        />
      </div>
      <div 
        className="timeline-tracks" 
        style={{ 
          width: totalFrames * FRAME_WIDTH,
          '--frame-width': `${FRAME_WIDTH}px`
        } as React.CSSProperties}
      >
        {displayLayers.map((layer) => {
          const ctxFrame = selectedFrame ?? currentFrame;
          return (
            <ContextMenu.Root key={layer.id}>
              <ContextMenu.Trigger>
                <div 
                  className={`timeline-track ${currentLayer?.id === layer.id ? 'selected' : ''}`}
                  onMouseDown={(e) => handleTrackMouseDown(e, layer)}
                  onDoubleClick={(e) => handleTrackDoubleClick(e, layer.id)}
                  onMouseMove={handleTrackMouseMove}
                  onMouseLeave={handleTrackMouseLeave}
                  onContextMenu={(e) => {
                    const frame = getFrameFromEvent(e);
                    setSelectedTween(null);
                    setSelectedFrame(frame);
                    clearSelection();
                    setCurrentLayer(layer);
                    setSelectedLayerId(String(layer.id));
                    onFrameClick(frame);
                  }}
                >
                  {(tweens[layer.id] ?? []).map((tw) => {
                    // Check if this tween is being resized by a dragging keyframe
                    let displayStartFrame = tw.startFrame;
                    let displayEndFrame = tw.endFrame;

                    if (draggingKeyframe?.layerId === layer.id) {
                      const affectedTween = draggingKeyframe.affectedTweens.find(at => at.tweenId === tw.id);
                      if (affectedTween) {
                        if (affectedTween.isStart) {
                          displayStartFrame = draggingKeyframe.newFrame;
                        }
                        if (affectedTween.isEnd) {
                          displayEndFrame = draggingKeyframe.newFrame;
                        }
                      }
                    }

                    return (
                      <div
                        key={tw.id}
                        className={`timeline-tween ${selectedTween?.layerId === layer.id && selectedTween?.startFrame === tw.startFrame ? 'selected' : ''}`}
                        style={{
                          '--tween-left': `${displayStartFrame * FRAME_WIDTH}px`,
                          '--tween-width': `${(displayEndFrame - displayStartFrame) * FRAME_WIDTH}px`
                        } as React.CSSProperties}
                        onMouseDown={(e) => handleTweenMouseDown(e, layer.id, tw)}
                        onDoubleClick={(e) => e.stopPropagation()}
                        title={t('timeline.tweenTitle', { start: displayStartFrame, end: displayEndFrame, easing: tw.easing })}
                      />
                    );
                  })}
                  {(keyframes[layer.id] ?? []).map((kf) => {
                    let displayFrame = kf.frame;
                    if (draggingKeyframe?.layerId === layer.id && draggingKeyframe.frame === kf.frame) {
                      displayFrame = draggingKeyframe.newFrame;
                    }
                    const isDeleting = deletingKeyframes.has(kf.id);
                    const isDragging =
                      draggingKeyframe?.layerId === layer.id && draggingKeyframe.frame === kf.frame;
                    
                    return (
                      <div
                        key={kf.id}
                        className={`timeline-keyframe ${selectedKeyframe?.layerId === layer.id && selectedKeyframe?.frame === kf.frame ? 'selected' : ''} ${isDragging ? 'dragging' : ''}`}
                        style={{ '--keyframe-left': `${displayFrame * FRAME_WIDTH}px` } as React.CSSProperties}
                        onMouseDown={(e) => handleKeyframeMouseDown(e, layer.id, kf.frame)}
                        onDoubleClick={(e) => handleKeyframeDoubleClick(e, layer.id, kf.frame)}
                        title={isDeleting ? t('timeline.frameDeleting', { frame: kf.frame }) : t('timeline.frameTitle', { frame: kf.frame })}
                      />
                    );
                  })}
                </div>
              </ContextMenu.Trigger>
              <ContextMenu.Content>
                <ContextMenu.Item
                  disabled={hasKeyframeAt(layer.id, ctxFrame)}
                  onSelect={() => handleContextAddKeyframe(layer.id, ctxFrame)}
                >
                  {t('timeline.addKeyframeAt', { frame: ctxFrame })}
                </ContextMenu.Item>
                <ContextMenu.Item
                  disabled={!hasKeyframeAt(layer.id, ctxFrame)}
                  onSelect={() => handleContextDeleteKeyframe(layer.id, ctxFrame)}
                >
                  {t('timeline.deleteKeyframe')}
                </ContextMenu.Item>
                <ContextMenu.Separator />
                <ContextMenu.Item
                  disabled={!canAddTweenAtFrame(layer.id, ctxFrame, keyframes, tweens)}
                  onSelect={() => handleContextAddTween(layer.id, ctxFrame)}
                >
                  {t('timeline.addTweenShort')}
                </ContextMenu.Item>
                <ContextMenu.Item
                  disabled={!hasTweenAt(layer.id, ctxFrame)}
                  onSelect={() => handleContextDeleteTween(layer.id, ctxFrame)}
                >
                  {t('timeline.deleteTween')}
                </ContextMenu.Item>
                <ContextMenu.Separator />
                <ContextMenu.Item
                  disabled={(keyframes[layer.id] ?? []).length === 0 || (tweens[layer.id] ?? []).length > 0}
                  color="red"
                  onSelect={() => handleClearAllKeyframes(layer.id)}
                >
                  {t('timeline.clearAllKeyframes')}
                </ContextMenu.Item>
                <ContextMenu.Item
                  disabled={(tweens[layer.id] ?? []).length === 0}
                  color="red"
                  onSelect={() => handleClearAllTweens(layer.id)}
                >
                  {t('timeline.clearAllTweens')}
                </ContextMenu.Item>
              </ContextMenu.Content>
            </ContextMenu.Root>
          );
        })}
        <div
          className="timeline-cursor"
          style={{ '--cursor-left': `${currentFrame * FRAME_WIDTH}px` } as React.CSSProperties}
        />
        {hoveredFrame !== null && hoveredFrame !== selectedFrame && (
          <div
            className="timeline-hover-frame"
            style={{ '--hover-left': `${hoveredFrame * FRAME_WIDTH}px` } as React.CSSProperties}
          />
        )}
        {selectedFrame !== null && (
          <div
            className="timeline-selected-frame"
            style={{ '--selected-left': `${selectedFrame * FRAME_WIDTH}px` } as React.CSSProperties}
          >
            {selectedTrackIndex !== null && (
              <span
                className="timeline-selected-frame-indicator"
                style={{
                  top: `${selectedTrackIndex * 28}px`,
                } as React.CSSProperties}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function applyEasing(t: number, easing: string): number {
  switch (easing) {
    case 'easeIn':
      return t * t;
    case 'easeOut':
      return t * (2 - t);
    case 'easeInOut':
      return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
    case 'bounce': {
      if (t < 1 / 2.75) {
        return 7.5625 * t * t;
      } else if (t < 2 / 2.75) {
        const t1 = t - 1.5 / 2.75;
        return 7.5625 * t1 * t1 + 0.75;
      } else if (t < 2.5 / 2.75) {
        const t2 = t - 2.25 / 2.75;
        return 7.5625 * t2 * t2 + 0.9375;
      } else {
        const t3 = t - 2.625 / 2.75;
        return 7.5625 * t3 * t3 + 0.984375;
      }
    }
    case 'elastic':
      return t === 0 ? 0 : t === 1 ? 1 : -Math.pow(2, 10 * t - 10) * Math.sin((t * 10 - 10.75) * ((2 * Math.PI) / 3));
    case 'back': {
      const c1 = 1.70158;
      const c3 = c1 + 1;
      return c3 * t * t * t - c1 * t * t;
    }
    default:
      return t;
  }
}
